import React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';
import { Card, Input } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import { useNotify } from '../../hooks';
import { Tax } from '../../models';
import { FormButtons, FormHeader } from '../shared/Forms';
import { CREATE_TAX, UPDATE_TAX } from '../../graphql/mutation';
import { validateTaxForm } from '../../lib/formValidations';

interface TaxFormProps {
  tax?: Tax;
}

function TaxForm({ tax }: TaxFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [disabled, setDisabled] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<Tax>>(tax ?? {});

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.preventDefault();
    const { value, name } = e.target;
    setFormState({
      ...formState,
      [name]: value,
    });
  };

  const [updateTaxMutation] = useMutation<{
    updateTax: {
      recordId: string;
    };
  }>(UPDATE_TAX);

  const [createTaxMutation] = useMutation<{
    createTax: {
      recordId: string;
    };
  }>(CREATE_TAX);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      const error = validateTaxForm(formState);
      if (error) return notify(error, 'error');
      // build object
      const taxData = {
        ...formState,
        value: Number(formState.value),
      };
      // create or update tax
      if (tax?._id) {
        await updateTax(taxData);
      } else {
        await createTax(taxData);
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createTax = async (taxData: Partial<Tax>) => {
    const { data } = await createTaxMutation({
      variables: {
        record: {
          ...taxData,
        },
      },
    });
    if (data?.createTax?.recordId) {
      notify('Impuesto creado con éxito', 'success');
      router.push('/app/settings/taxes');
    } else {
      notify('Ha ocurrido un error al crear el impuesto', 'error');
    }
  };

  const updateTax = async (taxData: Partial<Tax>) => {
    delete taxData._id;
    const { data } = await updateTaxMutation({
      variables: {
        filter: {
          _id: tax._id,
        },
        record: {
          ...taxData,
          __typename: undefined,
        },
      },
    });
    if (data?.updateTax?.recordId) {
      notify('Impuesto actualizado con éxito', 'success');
      router.push('/app/settings/taxes');
    } else {
      notify('Ha ocurrido un error al actualizar el impuesto', 'error');
    }
  };

  const changeTaxStatus = async () => updateTax({ active: !tax.active });

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <Card>
        <FormHeader
          href="/app/settings/taxes"
          backBtnText="Impuestos"
          title={formState._id ? 'Actualizar impuesto' : 'Crear impuestos'}
        />
        <Card.Body>
          <div className="w-full flex md:flex-row flex-col gap-5">
            <div className="w-full">
              <Input
                name="name"
                value={formState.name ?? ''}
                label="Nombre"
                type="text"
                placeholder="Ingrese un nombre"
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="w-full">
              <Input
                name="value"
                value={formState.value ?? ''}
                label="Valor porcentual"
                type="text"
                placeholder="Ingrese el valor porcentual"
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </Card.Body>
      </Card>
      <FormButtons
        type={tax ? 'update' : 'create'}
        toggleActive={changeTaxStatus}
        active={tax?.active}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default TaxForm;
